import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, getCurrentToken, API_BASE } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD } from '../table-management/designTokens';
import { FillGapModal } from './FillGapModal';

interface AuditRow {
  referenceId: string;
  purchaserName: string;
  purchaserEmail: string;
  amount: string | null;
  ticketCount: number;
  reportedTicketCount: number | null;
  orderTicketCount: number;
  actualTickets: number;
  tierName: string;
  tierPrice: number | null;
  expectedTix: number | null;
  gap: number;
  revenueGap: number | null;
}

export function OrderTicketAudit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'gaps' | 'countGaps' | 'revenueGaps'>('gaps');
  const [fillGapRow, setFillGapRow] = useState<AuditRow | null>(null);
  const [backfillStatus, setBackfillStatus] = useState('');
  const backfillRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(() => {
    setLoading(true);
    apiClient<AuditRow[]>('/legacy-ball/orders/audit')
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(e => setError(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleBackfillUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBackfillStatus('Uploading...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getCurrentToken();
      const res = await fetch(`${API_BASE}/order-import/backfill-reported-count`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const result = await res.json();
      setBackfillStatus(`Updated ${result.updated} orders`);
      reload();
    } catch (err: any) {
      setBackfillStatus(`Error: ${err.message?.substring(0, 60)}`);
    }
    e.target.value = '';
    setTimeout(() => setBackfillStatus(''), 6000);
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>Loading audit data...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#dc2626' }}>Error: {error}</div>;

  const filtered = filter === 'gaps' ? rows.filter(r => r.gap > 0)
    : filter === 'countGaps' ? rows.filter(r => r.gap > 0 && r.reportedTicketCount !== null)
    : filter === 'revenueGaps' ? rows.filter(r => r.revenueGap !== null && r.revenueGap > 0)
    : rows;
  const totalGap = filtered.reduce((sum, r) => sum + Math.max(r.gap, 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: TEXT_DARK }}>Order / Ticket Audit</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: TEXT_MUTED }}>
            {filtered.length} orders shown · {totalGap} missing ticket{totalGap !== 1 ? 's' : ''} identified
          </p>
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button onClick={() => setFilter('countGaps')} style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: '14px', border: 'none', cursor: 'pointer', backgroundColor: filter === 'countGaps' ? PRIMARY : '#e5e7eb', color: filter === 'countGaps' ? '#fff' : '#374151' }}>Count Gaps</button>
          <button onClick={() => setFilter('revenueGaps')} style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: '14px', border: 'none', cursor: 'pointer', backgroundColor: filter === 'revenueGaps' ? '#6b4c9a' : '#e5e7eb', color: filter === 'revenueGaps' ? '#fff' : '#374151' }}>Revenue Gaps</button>
          <button onClick={() => setFilter('gaps')} style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: '14px', border: 'none', cursor: 'pointer', backgroundColor: filter === 'gaps' ? '#dc2626' : '#e5e7eb', color: filter === 'gaps' ? '#fff' : '#374151' }}>Any Gap</button>
          <button onClick={() => setFilter('all')} style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: '14px', border: 'none', cursor: 'pointer', backgroundColor: filter === 'all' ? '#374151' : '#e5e7eb', color: filter === 'all' ? '#fff' : '#374151' }}>All</button>
        </div>
      </div>

      {/* Backfill Reported Ticket Count */}
      <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f9fafb', border: '1px solid ' + BORDER_LIGHT, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8rem', color: TEXT_MUTED, fontWeight: 500 }}>Backfill ticket counts from file:</span>
        <input ref={backfillRef} type="file" accept=".xlsx,.csv,.xls" onChange={handleBackfillUpload} style={{ fontSize: '0.8rem' }} />
        {backfillStatus && <span style={{ fontSize: '0.78rem', fontWeight: 600, color: backfillStatus.startsWith('Error') ? '#dc2626' : '#059669' }}>{backfillStatus}</span>}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid ' + BORDER_LIGHT, borderRadius: '10px', backgroundColor: BG_CARD }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid ' + BORDER_LIGHT, backgroundColor: '#f9fafb' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ref #</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Purchaser</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Amount</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Tier</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Purchased</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Expected</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Actual</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Count Gap</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Rev Gap</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr
                key={r.referenceId}
                style={{ borderBottom: '1px solid ' + BORDER_LIGHT, cursor: r.gap > 0 ? 'pointer' : 'default' }}
                onClick={() => r.gap > 0 && setFillGapRow(r)}
              >
                <td style={{ padding: '10px 12px' }}>{r.referenceId}</td>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.purchaserName}</td>
                <td style={{ padding: '10px 12px' }}>{r.amount ? (r.amount.startsWith('$') ? r.amount : `$${Number(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`) : '—'}</td>
                <td style={{ padding: '10px 12px' }}>{r.tierName}</td>
                <td style={{ padding: '10px 12px' }}>{r.ticketCount}</td>
                <td style={{ padding: '10px 12px' }}>{r.expectedTix !== null ? r.expectedTix : '—'}</td>
                <td style={{ padding: '10px 12px' }}>{r.actualTickets}</td>
                <td style={{ padding: '10px 12px', fontWeight: 700, color: r.gap > 0 ? '#dc2626' : '#059669' }}>
                  {r.gap > 0 ? '-' + r.gap : r.gap === 0 ? String.fromCharCode(10003) : '+' + Math.abs(r.gap)}
                </td>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: r.revenueGap !== null && r.revenueGap > 0 ? '#6b4c9a' : '#6b7280' }}>
                  {r.revenueGap !== null ? (r.revenueGap > 0 ? '-' + r.revenueGap : r.revenueGap === 0 ? String.fromCharCode(10003) : '+' + Math.abs(r.revenueGap)) : '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>No gaps found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {fillGapRow && (
        <FillGapModal
          referenceId={fillGapRow.referenceId}
          purchaserName={fillGapRow.purchaserName}
          tierName={fillGapRow.tierName}
          amount={fillGapRow.amount}
          gap={fillGapRow.gap}
          existingTickets={fillGapRow.actualTickets}
          onClose={() => setFillGapRow(null)}
          onComplete={() => { setFillGapRow(null); reload(); }}
        />
      )}
    </div>
  );
}
